import React, { useState, useEffect, useRef } from "react";
import { configService } from "../services/configService";
import toast from "react-hot-toast";
import { Truck, Package, Save, X, ChevronLeft, ChevronRight, Search, RefreshCw } from "lucide-react";
import { isTokenInvalidationError, isLoggingOut } from "../utils/errorUtils";

interface RoutingInfo {
  id: number;
  edi_shipment_id: number;
  order_number: string;
  customer_name: string;
  status: string;
  created_at: string;
  scac?: string;
  carrier?: string;
  shipping_method?: string;
  bill_of_lading?: string;
  pro_number?: string;
  trailer_number?: string;
  cost?: number;
  gs1_company_prefix?: string;
  courier?: string;
  account_number?: string;
}

const WholesaleRouting: React.FC = () => {
  const [items, setItems] = useState<RoutingInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedItem, setSelectedItem] = useState<RoutingInfo | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  // Mode state
  const [syncMode, setSyncMode] = useState<"live" | "test">("live");
  const [isModeLoading, setIsModeLoading] = useState(true);
  const [isUpdatingMode, setIsUpdatingMode] = useState(false);

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Load sync mode on mount
  useEffect(() => {
    loadSyncMode();
  }, []);

  const loadSyncMode = async () => {
    try {
      setIsModeLoading(true);
      const modeConfig = await configService.getEdiShipmentSyncMode();
      setSyncMode(modeConfig.mode);
    } catch (error) {
      console.error("Failed to load sync mode:", error);
    } finally {
      setIsModeLoading(false);
    }
  };

  const handleModeChange = async (newMode: "live" | "test") => {
    if (newMode === syncMode) return;
    try {
      setIsUpdatingMode(true);
      await configService.updateEdiShipmentSyncMode(newMode);
      setSyncMode(newMode);
      toast.success(`EDI shipment sync mode set to ${newMode.toUpperCase()}`);
      setPage(1);
      // fetchItems will be triggered by useEffect on syncMode change if we add it to dependency
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(error?.message || "Failed to update sync mode");
      }
      console.error("Failed to update sync mode:", error);
    } finally {
      setIsUpdatingMode(false);
    }
  };

  // Form state
  const [formData, setFormData] = useState({
    scac: "",
    carrier: "",
    shipping_method: "",
    bill_of_lading: "",
    pro_number: "",
    trailer_number: "",
    cost: "",
    gs1_company_prefix: "",
    courier: "",
    account_number: ""
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const getFieldError = (name: string, value: string) => {
    switch (name) {
      case "scac":
        if (!value.trim()) return "SCAC is required";
        break;
      case "carrier":
        if (!value.trim()) return "Carrier is required";
        break;
      case "shipping_method":
        if (!value.trim()) return "Shipping Method is required";
        break;
      case "bill_of_lading":
        if (!value.trim()) return "Bill of Lading is required";
        break;
      case "pro_number":
        if (!value.trim()) return "PRO Number is required";
        if (!/^[A-Za-z0-9]{7,10}$/.test(value)) return "PRO Number must be 7-10 alphanumeric characters";
        break;
      case "trailer_number":
        if (!value.trim()) return "Trailer Number is required";
        break;
      case "cost":
        if (!value.toString().trim()) return "Cost is required";
        const costNum = Number(value);
        if (isNaN(costNum)) return "Cost must be a number";
        if (costNum < 0) return "Cost cannot be negative";
        break;
      case "gs1_company_prefix":
        if (!value.trim()) return "GS1 Prefix is required";
        if (!/^\d{5,12}$/.test(value)) return "GS1 Prefix must be 5-12 digits";
        break;
      case "courier":
        if (!value.trim()) return "Courier is required";
        break;
      case "account_number":
        const accNum = Number(value);
        if (!value.trim()) return "Account Number is required";
        if (isNaN(accNum)) return "Account Number must be a number";
        break;
    }
    return "";
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    Object.keys(formData).forEach((key) => {
      const error = getFieldError(key, (formData as any)[key]);
      if (error) {
        newErrors[key] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchItems = async (pageNum = page) => {
    // Don't fetch if mode is loading
    if (isModeLoading) return;

    setIsLoading(true);
    try {
      const data = await configService.getWholesaleRouting(pageNum, perPage, statusFilter, debouncedSearchQuery, syncMode);
      if (data) {
        setItems(data.items || []);
        setTotalPages(data.total_pages || 1);
        setTotalItems(data.total || 0);
        // Ensure we update page state if the backend returned a different page (e.g. if requested page was out of bounds)
        if (data.page !== pageNum) {
          setPage(data.page);
        }
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to fetch routing tasks");
    } finally {
      setIsLoading(false);
    }
  };

  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      await fetchItems(page);
    } catch (error) {
      console.error("Failed to refresh data:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (!isModeLoading) {
      fetchItems(page);
    }
  }, [statusFilter, debouncedSearchQuery, page, syncMode, isModeLoading]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleEdit = (item: RoutingInfo) => {
    setSelectedItem(item);
    setErrors({});
    setFormData({
      scac: item.scac || "",
      carrier: item.carrier || "",
      shipping_method: item.shipping_method || "",
      bill_of_lading: item.bill_of_lading || "",
      pro_number: item.pro_number || "",
      trailer_number: item.trailer_number || "",
      cost: item.cost ? String(item.cost) : "",
      gs1_company_prefix: item.gs1_company_prefix || "",
      courier: item.courier || "",
      account_number: item.account_number || ""
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) return;

    if (!validateForm()) return;

    setIsSaving(true);
    try {
      await configService.updateWholesaleRouting(selectedItem.id, {
        ...formData,
        cost: formData.cost ? parseFloat(formData.cost) : null
      });
      toast.success("Routing info updated successfully");
      setIsModalOpen(false);
      fetchItems(page); // Refresh current page
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update routing info");
    } finally {
      setIsSaving(false);
    }
  };

  const pageButtons = () => {
    const buttons = [] as number[];
    const start = Math.max(1, Math.min(page - 2, Math.max(1, totalPages - 4)));
    const end = Math.min(totalPages, start + 4);
    for (let p = start; p <= end; p++) buttons.push(p);
    return buttons;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    const error = getFieldError(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">EDI Routing Info</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage pending routing information for packed EDI shipments
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-end sm:items-center">
          {/* Mode control */}
          <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-md border">
            <span className="text-sm font-medium text-gray-700">Mode</span>
            {isModeLoading ? (
              <div className="inline-flex items-center px-3 py-1.5 text-sm text-gray-500">
                <RefreshCw className="h-4 w-4 mr-2 text-primary-600 animate-spin" />
                Loading...
              </div>
            ) : (
              <div className="inline-flex rounded border overflow-hidden">
                <button
                  onClick={() => handleModeChange("live")}
                  disabled={isUpdatingMode}
                  className={`${
                    syncMode === "live"
                      ? "bg-primary-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  } px-3 py-1.5 text-sm font-medium focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Live
                </button>
                <button
                  onClick={() => handleModeChange("test")}
                  disabled={isUpdatingMode}
                  className={`${
                    syncMode === "test"
                      ? "bg-primary-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  } px-3 py-1.5 text-sm font-medium border-l focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Test
                </button>
              </div>
            )}
            {isUpdatingMode && (
              <RefreshCw className="ml-1 h-4 w-4 animate-spin text-primary-600" />
            )}
          </div>

          <button
            onClick={refreshData}
            disabled={isRefreshing}
            className="inline-flex items-center px-3 py-2 rounded-md text-sm border bg-white hover:bg-gray-50"
          >
            <RefreshCw className={`h-5 w-5 mr-2 ${isRefreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters row */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Search order or customer..."
            className="block w-full pl-10 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="block pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 rounded-md bg-white border"
          >
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="text-sm text-gray-700">
        <span className="mr-6">
          <span className="text-gray-500">Total Tasks:</span>{" "}
          <span className="font-semibold">{totalItems}</span>
        </span>
        <span className="mr-6">
          <span className="text-gray-500">Status:</span>{" "}
          <span className="font-semibold capitalize">{statusFilter}</span>
        </span>
      </div>

      {/* Main Content */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Routing Tasks</h3>
            {!isModeLoading && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Showing tasks for:</span>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  syncMode === "live" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"
                }`}>
                  {syncMode.toUpperCase()} mode
                </span>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="h-8 w-8 text-primary-600 animate-spin" />
            </div>
          ) : (items || []).length === 0 ? (
            <div className="text-center py-12">
              <Truck className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No routing tasks found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {statusFilter === 'pending'
                  ? "All shipments have routing information."
                  : "Try adjusting your search or filter."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Order Number
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Customer
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      EDI Carrier
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      BOL
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Created At
                    </th>
                    {statusFilter !== 'completed' && (
                      <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {(items || []).map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                        {item.order_number}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-700">
                        <div className="max-w-xs truncate" title={item.customer_name}>
                          {item.customer_name}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          item.status === 'completed' ? 'bg-green-100 text-green-800' : 
                          item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-700">
                        {item.carrier || "-"}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-700">
                        {item.bill_of_lading || "-"}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-700 whitespace-nowrap">
                        {formatDate(item.created_at)}
                      </td>
                      {statusFilter !== 'completed' && (
                        <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {item.status !== 'completed' && (
                            <button
                              onClick={() => handleEdit(item)}
                              className="text-primary-600 hover:text-primary-900 border border-primary-200 px-3 py-1 rounded hover:bg-primary-50 transition-colors"
                            >
                              Add Info
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{page}</span> of{" "}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav
                    className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {pageButtons().map((p) => (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${p === page
                          ? "z-10 bg-primary-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                          : "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                          }`}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-start">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Update Routing Info: {selectedItem?.order_number}
                  </h3>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <span className="sr-only">Close</span>
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <form id="routing-form" onSubmit={handleSubmit} className="mt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="scac" className="block text-sm font-medium text-gray-700">SCAC</label>
                      <input
                        type="text"
                        name="scac"
                        id="scac"
                        value={formData.scac}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full border ${errors.scac ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"} rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm`}
                      />
                      {errors.scac && <p className="mt-1 text-sm text-red-600">{errors.scac}</p>}
                    </div>
                    <div>
                      <label htmlFor="carrier" className="block text-sm font-medium text-gray-700">Carrier</label>
                      <input
                        type="text"
                        name="carrier"
                        id="carrier"
                        value={formData.carrier}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full border ${errors.carrier ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"} rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm`}
                      />
                      {errors.carrier && <p className="mt-1 text-sm text-red-600">{errors.carrier}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="shipping_method" className="block text-sm font-medium text-gray-700">Shipping Method</label>
                      <input
                        type="text"
                        name="shipping_method"
                        id="shipping_method"
                        value={formData.shipping_method}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full border ${errors.shipping_method ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"} rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm`}
                      />
                      {errors.shipping_method && <p className="mt-1 text-sm text-red-600">{errors.shipping_method}</p>}
                    </div>
                    <div>
                      <label htmlFor="bill_of_lading" className="block text-sm font-medium text-gray-700">Bill of Lading</label>
                      <input
                        type="text"
                        name="bill_of_lading"
                        id="bill_of_lading"
                        value={formData.bill_of_lading}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full border ${errors.bill_of_lading ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"} rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm`}
                      />
                      {errors.bill_of_lading && <p className="mt-1 text-sm text-red-600">{errors.bill_of_lading}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="pro_number" className="block text-sm font-medium text-gray-700">PRO Number</label>
                      <input
                        type="text"
                        name="pro_number"
                        id="pro_number"
                        value={formData.pro_number}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full border ${errors.pro_number ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"} rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm`}
                      />
                      {errors.pro_number && <p className="mt-1 text-sm text-red-600">{errors.pro_number}</p>}
                    </div>
                    <div>
                      <label htmlFor="trailer_number" className="block text-sm font-medium text-gray-700">Trailer Number</label>
                      <input
                        type="text"
                        name="trailer_number"
                        id="trailer_number"
                        value={formData.trailer_number}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full border ${errors.trailer_number ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"} rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm`}
                      />
                      {errors.trailer_number && <p className="mt-1 text-sm text-red-600">{errors.trailer_number}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="cost" className="block text-sm font-medium text-gray-700">Cost</label>
                      <input
                        type="number"
                        step="0.01"
                        name="cost"
                        id="cost"
                        value={formData.cost}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full border ${errors.cost ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"} rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm`}
                      />
                      {errors.cost && <p className="mt-1 text-sm text-red-600">{errors.cost}</p>}
                    </div>
                    <div>
                      <label htmlFor="gs1_company_prefix" className="block text-sm font-medium text-gray-700">GS1 Prefix</label>
                      <input
                        type="text"
                        name="gs1_company_prefix"
                        id="gs1_company_prefix"
                        value={formData.gs1_company_prefix}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full border ${errors.gs1_company_prefix ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"} rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm`}
                      />
                      {errors.gs1_company_prefix && <p className="mt-1 text-sm text-red-600">{errors.gs1_company_prefix}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="courier" className="block text-sm font-medium text-gray-700">Courier</label>
                      <input
                        type="text"
                        name="courier"
                        id="courier"
                        value={formData.courier}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full border ${errors.courier ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"} rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm`}
                      />
                      {errors.courier && <p className="mt-1 text-sm text-red-600">{errors.courier}</p>}
                    </div>
                    <div>
                      <label htmlFor="account_number" className="block text-sm font-medium text-gray-700">Account Number</label>
                      <input
                        type="text"
                        name="account_number"
                        id="account_number"
                        value={formData.account_number}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full border ${errors.account_number ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"} rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm`}
                      />
                      {errors.account_number && <p className="mt-1 text-sm text-red-600">{errors.account_number}</p>}
                    </div>
                  </div>
                </form>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="submit"
                  form="routing-form"
                  disabled={isSaving}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm ${isSaving ? "opacity-75 cursor-not-allowed" : ""
                    }`}
                >
                  {isSaving ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WholesaleRouting;